import {Injectable} from '@angular/core';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {PlasticsMachineType} from '@domain/machine/plastics-machine-type';
import {GridColorPreviewComponent} from '@presentation/pages/textile-data/color/overview/grid-color-preview/grid-color-preview.component';
import {
  ColDefBuilderFactoryService,
  ContentSwitcherNavigationInput,
  CrudColdef,
  GridOptionsBuilder,
  LinkIconRendererComponent,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  StringUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {ICellRendererParams, ITooltipParams, ValueGetterParams} from 'ag-grid-community';
import {Subject} from 'rxjs';

@Injectable({providedIn: 'root'})
export class RejectReasonOverviewService {
  private readonly pageUrls = LastModifiedCardUtils.getPageUrls('rejectReason');
  private readonly REJECT_REASON_TRANSLATION_KEY = 'BMSCONFIG.REJECT_REASON.REJECT_REASON';

  public constructor(
    private readonly colDefBuilderFactory: ColDefBuilderFactoryService,
    private readonly translate: TranslateService
  ) {}

  public getColumnDefs(withLinkIcon: boolean = true, rowActionsComplete?: Subject<void>): CrudColdef {
    let nameColDef = this.colDefBuilderFactory.getBuilder().withColIdAndField('name', true).withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator);

    if (withLinkIcon) {
      const editRoute = NavigationUtils.getNavigationRoute(NavigationId.EDIT_REJECT_REASON);

      nameColDef = nameColDef.withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
        return {
          params,
          editPageUrl: RouteUtils.paths.settings.rejectReason.editRejectReason.path,
          rowActionsComplete,
          contentSwitcherEntry: {componentInfo: {component: editRoute.component}, data: editRoute.data} as ContentSwitcherNavigationInput
        };
      });
    }

    return {
      colDefs: [
        nameColDef.build(),
        this.colDefBuilderFactory
          .getBuilder()
          .withHeaderName('GENERAL.NAME')
          .withColIdAndField('name', true)
          .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
            return {
              params,
              editPageUrl: this.pageUrls.edit
            };
          })
          .withComparator(StringUtils.stringComparator)
          .build(),
        this.colDefBuilderFactory.getBuilder().withHeaderName('GENERAL.CODE').withColIdAndField('code', true).build(),
        this.colDefBuilderFactory.getBuilder().withHeaderName('GENERAL.DESCRIPTION').withColIdAndField('description', true).withComparator(StringUtils.stringComparator).build(),
        this.colDefBuilderFactory.getBuilder().withHeaderName('BMSCONFIG.REJECT_REASON.DISPLAY_ORDER').withColIdAndField('displayOrder', true).build(),
        this.colDefBuilderFactory
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.COLOR.COLOR')
          .withColIdAndField('color')
          .withTooltipValueGetter((params: ITooltipParams) => params.value.getRgbAsCommaSeparatedValue())
          .withCellRenderer(GridColorPreviewComponent, {showRGBValue: true})
          .withoutFilter()
          .build(),
        this.colDefBuilderFactory
          .getBuilder()
          .withHeaderName('BMSCONFIG.REJECT_REASON.REJECT_TYPE.REJECT_TYPE')
          .withColId('rejectType')
          .withValueGetter((params: ValueGetterParams) => this.translate.instant(`BMSCONFIG.REJECT_REASON.REJECT_TYPE.${params.data.rejectType}`), true)
          .withComparator(StringUtils.stringComparator)
          .build(),
        this.colDefBuilderFactory
          .getBuilder()
          .withHeaderName('BMSCONFIG.REJECT_REASON.MACHINE_TYPES')
          .withColId('machineTypes')
          .withValueGetter((params: ValueGetterParams) => params.data.machineTypes.map((machineType: PlasticsMachineType) => machineType.name), true)
          .withComparator(StringUtils.stringComparator)
          .build()
      ],
      rowActionsComplete
    } as CrudColdef;
  }

  public getDefaultGridOptionsBuilder(builder: GridOptionsBuilder): GridOptionsBuilder {
    return builder
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: this.translate.instant(this.REJECT_REASON_TRANSLATION_KEY, {count: 1}),
        hideDescription: true
      } as NoDataOverlayComponentParams);
  }
}
